import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Image,
  List,
  Switch,
  Text,
} from "@chakra-ui/react";
import React, { Suspense, useContext, useMemo } from "react";
import {
  Coordinate,
  DurationDistance,
  SpotStat,
} from "~/generated/graphql.types";
import logo from "~/assets/logo.svg";
import { SpotStatsSummary } from "./BetweenTwoSpotListItems/SpotStat/SpotStatSummary";
import { EditModeContext, ViewMode } from "../..";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import { SpotsTableModal } from "./SpotsTableModal";
import { convertParamsForSidebar } from "./convertType";
import { ExportCsvButton } from "./ExportCsvButtons";
import { SpotListItem } from "./SpotListItem";
import { BetweenTwoSpotListItems } from "./BetweenTwoSpotListItems";
import { Link } from "react-router-dom";
import { SpotForPreviewSidebarFragment } from "../../index.generated";

export const Sidebar = React.memo(
  ({
    courseId,
    courseName,
    start,
    dest,
    waypoints,
    statsAllSpots,
    statsWithoutOpt,
    isVisibleToAllUserGroups = false,
    onClickSpot,
  }: {
    courseId: string;
    courseName: string;
    start?: SpotForPreviewSidebarFragment;
    dest?: SpotForPreviewSidebarFragment;
    waypoints: Array<SpotForPreviewSidebarFragment>;
    statsAllSpots: SpotStat[] | undefined;
    statsWithoutOpt: SpotStat[] | undefined;
    isVisibleToAllUserGroups?: boolean;
    onClickSpot: (coordinate: Coordinate) => void;
  }) => {
    const { spotIdForEdit, setSpotIdForEdit, switchViewMode, viewMode } =
      useContext(EditModeContext);

    const { startSidebar, destSidebar, waypointsSidebar } = useMemo(() => {
      return convertParamsForSidebar({
        start,
        dest,
        waypoints,
      });
    }, [start, dest, waypoints]);

    const statsSummary = useMemo(() => {
      const stats: DurationDistance[] = [];
      if (statsAllSpots == undefined) return stats;
      statsAllSpots.forEach((value) => {
        stats.push(value.durationDistance);
      });
      return stats;
    }, [statsAllSpots]);

    return (
      <Suspense fallback={<Text>Loading...</Text>}>
        <Box
          p={6}
          bgColor="#0F2446"
          color="white"
          overflowY="scroll"
          h="100vh"
          sx={{
            "&::-webkit-scrollbar": {
              width: 2,
              backgroundColor: "blue.900",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: `blue.400`,
            },
          }}
        >
          <Link to={`/`}>
            <Image src={logo} marginBottom="1rem" w="24rem" />
          </Link>
          <Flex justifyContent={"space-between"} alignItems="center">
            <Text as="h3" size="md" fontWeight="bold" fontSize={20}>
              {courseName}
            </Text>
          </Flex>
          <ButtonGroup colorScheme="blue" w="full" my={4} alignItems="center">
            <Button
              flex={1}
              onClick={() => {
                switchViewMode(ViewMode.spotsView);
              }}
              variant={viewMode === ViewMode.spotsView ? "solid" : "outline"}
            >
              スポットを閲覧
            </Button>
            <Button
              flex={1}
              onClick={() => {
                switchViewMode(ViewMode.voiceView);
              }}
              variant={viewMode === ViewMode.voiceView ? "solid" : "outline"}
            >
              ナレーションを閲覧
            </Button>
            <SpotsTableModal courseId={courseId} />
          </ButtonGroup>

          <List my={2}>
            {startSidebar ? (
              <SpotListItem
                spot={startSidebar}
                onClick={() => {
                  onClickSpot(startSidebar.coordinate);
                  setSpotIdForEdit(startSidebar.id);
                }}
                isSelected={spotIdForEdit === startSidebar.id}
              />
            ) : null}
            {startSidebar && destSidebar ? (
              <BetweenTwoSpotListItems
                prevSpot={startSidebar}
                nextSpot={waypointsSidebar[0] || destSidebar}
                allSpotsStats={
                  statsAllSpots?.find(
                    (s) => s.statsAfterSpotId === startSidebar.id
                  )?.durationDistance
                }
                withoutOptionalSpotsStats={
                  statsWithoutOpt?.find(
                    (s) => s.statsAfterSpotId === startSidebar.id
                  )?.durationDistance
                }
              />
            ) : null}

            {waypointsSidebar.map((spot, i) => (
              <div key={spot.id}>
                {spot ? (
                  <>
                    <Box>
                      <SpotListItem
                        spot={spot}
                        isSelected={spotIdForEdit === spot.id}
                        onClick={() => {
                          onClickSpot(spot.coordinate);
                          setSpotIdForEdit(spot.id);
                        }}
                      />
                    </Box>
                    <BetweenTwoSpotListItems
                      prevSpot={waypointsSidebar[i]}
                      nextSpot={waypointsSidebar[i + 1] || dest}
                      allSpotsStats={
                        statsAllSpots?.find(
                          (s) => s.statsAfterSpotId === waypointsSidebar[i].id
                        )?.durationDistance
                      }
                      withoutOptionalSpotsStats={
                        statsWithoutOpt?.find(
                          (s) => s.statsAfterSpotId === waypointsSidebar[i].id
                        )?.durationDistance
                      }
                    />
                  </>
                ) : null}
              </div>
            ))}
            {destSidebar ? (
              <SpotListItem
                spot={destSidebar}
                isSelected={spotIdForEdit === destSidebar.id}
                onClick={() => {
                  onClickSpot(destSidebar.coordinate);
                  setSpotIdForEdit(destSidebar.id);
                }}
              />
            ) : null}
            {start && dest ? (
              <SpotStatsSummary allSpotStats={statsSummary} />
            ) : null}
          </List>
          <ExportCsvButton courseId={courseId} courseName={courseName} />
          {/* {start && dest ? (
        <CreateHyperlapseOnRouteModal
          courseId={courseId}
          start={start}
          dest={dest}
          waypoints={waypoints}
        />
      ) : null} */}
        </Box>
      </Suspense>
    );
  }
);
